/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';

import { Button, FullscreenDialog, Grid, breakpoints } from '@coursera/cds-core';

import _t from 'i18n!nls/staff-management';

import withNotifications from '../common/notifications/withNotifications';
import type {
  ErrorInterface,
  ObjectId,
  StaffManagementStoreV2 as StaffManagementStoreClass,
  StaffMembershipType,
} from '../store';
import { actions } from '../store';
import withErrorNotification from '../withErrorNotification';
import withFluxibleContextAsProps from '../withFluxibleContextAsProps';
import withShow from '../withShow';
import EditModalRolesCheckboxGroup from './EditModalRolesCheckboxGroup';

const { Header, HeadingGroup, Content, Actions } = FullscreenDialog;

type Props = {
  /** Called when user clicks cancel button. */
  cancelOnClick?: () => void;
  /** If true, cancel button cannot be clicked (no callback). */
  cancelDisabled?: boolean;

  /** Called when user clicks confirm button. */
  confirmOnClick?: () => void;
  /** If true, confirm button cannot be clicked (no callback). */
  confirmDisabled?: boolean;

  /** Callback to handle click, presecribed by CheckboxGroup. */
  onChange: (options: string[]) => void;

  /** If truthy, component is in an error state. */
  error?: ErrorInterface;

  /** Id of entity to load data from. */
  fromId?: ObjectId;

  /** Names of people being edited. */
  names?: Array<string>;

  roles?: Array<string>;

  /** One of the types supported by the staff-membershp component. Modifies messaging shown. */
  type?: keyof typeof StaffMembershipType;

  /** Creates a new toast notification */
  notify: () => void;
};

const styles = {
  button: () => css`
    margin-right: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      margin: var(--cds-spacing-100) 0;
      width: 100%;
    }
  `,
};

/**
 * Modal form displayed at the beginning of an edit flow.
 */
export const EditModal = ({
  cancelOnClick = _.noop,
  cancelDisabled = false,
  confirmOnClick = _.noop,
  confirmDisabled = false,
  onChange,
  fromId,
  names = [],
  roles = [],
  type = 'partner' as const,
}: Props) => {
  // We enable the "Save" action when the user modifies the roles available.
  // This is an additional UX block added later in development (just prior to PSM launch).
  const [initialRoles] = useState(roles);
  let editRolesListMessage = '';
  let title = '';
  if (type === 'partner') {
    editRolesListMessage = _t(
      `If you want to edit their program staff or course staff roles, you can do so in Settings for the respective program or course. `
    );
    title = _t('Edit Institution Staff Roles');
  } else if (type === 'program') {
    editRolesListMessage = _t(`
If you want to edit an institution or course role assignment, you will need to go to that institution or course. `);
    title = _t('Edit Program Staff Roles');
  }

  return (
    // At time of writing modal does not allow pass through of additional properties. To allow selection of modal components
    // we wrap entire contents in `data-e2e` tag.

    <div data-e2e={EditModal.e2eName}>
      <FullscreenDialog contentWidth="60%" open={true} onClose={cancelOnClick}>
        <Header />
        <div>
          <HeadingGroup heading={title} supportText={`for ${names.join(', ')}`} />
          <Content>
            <EditModalRolesCheckboxGroup action="edit" fromId={fromId} type={type} onChange={onChange} />
          </Content>
          <Actions>
            <Grid container alignItems="center" justifyContent="flex-start">
              <div css={styles.button}>
                <Button
                  data-e2e="btn-confirm"
                  disabled={confirmDisabled || _.isEqual(roles, initialRoles)}
                  onClick={confirmOnClick}
                  variant="primary"
                >
                  {_t('Update')}
                </Button>
              </div>
              <div css={styles.button}>
                <Button data-e2e="btn-cancel" disabled={cancelDisabled} onClick={cancelOnClick} variant="secondary">
                  {_t('Cancel')}
                </Button>
              </div>
            </Grid>
          </Actions>
        </div>
      </FullscreenDialog>
    </div>
  );
};

EditModal.e2eName = 'staff-management-edit-modal';

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction, notify }: $TSFixMe
): Partial<Props & { show: boolean }> => {
  const state = StaffManagementStoreV2.get();
  const { fromId, type } = state;
  const editFlowState = StaffManagementStoreV2.get().ui?.editFlow;
  const roles = editFlowState?.roles;
  const users = editFlowState?.users;
  const partnerRoleTemplatesState = StaffManagementStoreV2.partnerRoleTemplates();
  return {
    confirmDisabled: !users?.length || !roles?.length,
    cancelOnClick: () => {
      executeAction(actions.editFlow.prompt, { show: false });
    },
    confirmOnClick: () => {
      executeAction(actions.editFlow.confirm, { fromId, type, roles, users, notify });
    },
    error: _.get(partnerRoleTemplatesState, 'error'),
    fromId,
    names: _.map(users, (user) => user.fullName || ''),
    roles,
    show: !!users?.length,
    type,
  };
};

export default compose<Props, {}>(
  withFluxibleContextAsProps(),
  withNotifications(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector),
  withErrorNotification(),
  withShow()
)(EditModal);
